import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AlertTriangle } from 'lucide-react';
import {
  ModalOverlay,
  ModalPanel,
  ModalTitle,
  ModalSubtitle,
  ModalActions,
  ModalButton,
} from './shared/Modal';

export type ReprocessOperation =
  | 'boundary'
  | 'ocr'
  | 'page_numbers'
  | 'doc_metadata'
  | 'reorder'
  | 'duplicates';
export type ReprocessScope = 'case' | 'document';

const DEFAULT_OPERATIONS: ReprocessOperation[] = ['doc_metadata'];

// Render order for the option list (also drives the checkbox state shape).
const OPERATION_ORDER: ReprocessOperation[] = [
  'boundary', 'ocr', 'page_numbers', 'doc_metadata', 'reorder', 'duplicates',
];

interface ReprocessModalProps {
  visible: boolean;
  scope: ReprocessScope;
  targetLabel: string;
  onConfirm: (operations: ReprocessOperation[]) => void;
  onCancel: () => void;
  defaultSelection?: ReprocessOperation[];
  /** When set, blocks submit and shows an explanatory banner. */
  conflict?: { message: string } | null;
}

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const WarningBanner = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(234, 179, 8, 0.15);
  border: 1px solid rgba(234, 179, 8, 0.4);
  color: #92400e;
  font-size: 13px;
`;

const ConflictBanner = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(239, 68, 68, 0.10);
  border: 1px solid rgba(239, 68, 68, 0.35);
  color: #991b1b;
  font-size: 13px;
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 14px;
  color: #1f2937;
  cursor: pointer;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin-top: 2px;
`;

const OptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const OptionLabel = styled.div`
  font-weight: 500;
`;

const OptionDescription = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const WarningHeading = styled.div`
  font-weight: 500;
  margin-bottom: 4px;
`;

const WarningList = styled.ul`
  margin: 0;
  padding-left: 18px;
`;

const WarningFooter = styled.div`
  margin-top: 6px;
`;

const OPERATION_LABELS: Record<ReprocessOperation, string> = {
  boundary: 'Document boundaries',
  ocr: 'Full-text OCR',
  page_numbers: 'Page numbers',
  doc_metadata: 'Document metadata',
  reorder: 'Page order analysis',
  duplicates: 'Duplicate page detection',
};

const OPERATION_DESCRIPTIONS: Record<ReprocessOperation, string> = {
  boundary: 'Re-detect where each document starts and ends within the case.',
  ocr: 'Re-run full-text extraction on every page. Slow — only needed if OCR quality was poor.',
  page_numbers: 'Re-read the printed page number on each page (used by page order analysis).',
  doc_metadata: "Re-extract each document's category, title, and date from its text and apply them.",
  reorder: 'Analyse printed page numbers to detect displaced or misordered pages.',
  duplicates: 'Compare page text across documents to find possible duplicate pages.',
};

const OPERATION_WARNINGS: Record<ReprocessOperation, string | null> = {
  boundary: 'Discards all manual document splits, merges, and per-document edits in this case.',
  ocr: 'Overwrites existing OCR text for all pages. This is slow.',
  page_numbers: 'Overwrites manually edited page numbers.',
  doc_metadata: 'Overwrites manually edited categories, titles, and dates.',
  reorder: null,
  duplicates: null,
};

export const ReprocessModal: React.FC<ReprocessModalProps> = ({
  visible,
  scope,
  targetLabel,
  onConfirm,
  onCancel,
  defaultSelection = DEFAULT_OPERATIONS,
  conflict = null,
}) => {
  const buildSelection = (selected: ReprocessOperation[]) =>
    OPERATION_ORDER.reduce(
      (acc, op) => ({ ...acc, [op]: selected.includes(op) }),
      {} as Record<ReprocessOperation, boolean>,
    );

  const [selection, setSelection] = useState<Record<ReprocessOperation, boolean>>(
    () => buildSelection(DEFAULT_OPERATIONS),
  );

  useEffect(() => {
    if (visible) {
      setSelection(buildSelection(defaultSelection));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!visible) {
    return null;
  }

  const toggle = (operation: ReprocessOperation) => {
    setSelection(prev => ({
      ...prev,
      [operation]: !prev[operation],
    }));
  };

  const visibleOperations = OPERATION_ORDER
    .filter(op => scope === 'case' || op !== 'boundary');

  const selectedOperations = visibleOperations.filter(key => selection[key]);

  const scopeDescriptor = scope === 'document' ? 'document' : 'case';
  const scopeLabel = scope === 'document' ? 'Document' : 'Case';

  const handleConfirm = () => {
    if (selectedOperations.length === 0) {
      return;
    }
    onConfirm(selectedOperations);
  };

  // Collect warnings for currently selected operations
  const activeWarnings = selectedOperations
    .map(op => OPERATION_WARNINGS[op])
    .filter((w): w is string => w !== null);

  const hasDestructiveOps = activeWarnings.length > 0;

  return (
    <ModalOverlay role="dialog" aria-modal="true">
      <ModalPanel $width="480px">
        <TitleBlock>
          <ModalTitle>Re-run analysis</ModalTitle>
          <ModalSubtitle>
            {scopeLabel}: <strong>{targetLabel}</strong>
          </ModalSubtitle>
        </TitleBlock>

        {conflict && (
          <ConflictBanner>
            <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>{conflict.message}</div>
          </ConflictBanner>
        )}

        {hasDestructiveOps && (
          <WarningBanner>
            <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <WarningHeading>
                The selected operations will overwrite previous results:
              </WarningHeading>
              <WarningList>
                {activeWarnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </WarningList>
              <WarningFooter>
                Any manual edits to affected fields on this {scopeDescriptor} will be lost. This cannot be undone.
              </WarningFooter>
            </div>
          </WarningBanner>
        )}

        <OptionsList>
          {visibleOperations.map(operation => (
            <OptionRow key={operation} onClick={() => toggle(operation)}>
              <Checkbox
                type="checkbox"
                checked={selection[operation]}
                readOnly
              />
              <OptionText>
                <OptionLabel>{OPERATION_LABELS[operation]}</OptionLabel>
                <OptionDescription>
                  {OPERATION_DESCRIPTIONS[operation]}
                </OptionDescription>
              </OptionText>
            </OptionRow>
          ))}
        </OptionsList>

        <ModalActions>
          <ModalButton type="button" $variant="secondary" onClick={onCancel}>
            Cancel
          </ModalButton>
          <ModalButton
            type="button"
            $variant="primary"
            onClick={handleConfirm}
            disabled={selectedOperations.length === 0 || conflict !== null}
          >
            Re-run{selectedOperations.length > 0 ? ` (${selectedOperations.length})` : ''}
          </ModalButton>
        </ModalActions>
      </ModalPanel>
    </ModalOverlay>
  );
};

export default ReprocessModal;
